import { useMutation, useQueryClient } from 'react-query';

import { AdHocFile } from '../../../../backend/src/matter/ad-hoc/ad-hoc-file.entity';
import type { ProceduralJurisdiction } from '../../../../backend/src/matter/procedural-timeline/procedural-timeline-enums';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { APIBaseChronos } from '@/api/hosts';
import { QueryEntity } from '@/api/types';
import useGetFetchConfig, { FetchMethod } from '@/api/useGetFetchConfig';

interface SubmitProceduralFileParams {
  matterId: string;
  uploadId: string;
  name: string;
  mimeType: string;
  size: number;
  timeZone: string;
  jurisdiction: ProceduralJurisdiction;
}

/**
 * Submits a staged procedural-timeline file. The backend moves it from its
 * temporary key to the permanent location and kicks off extraction. Pair with
 * {@link useStageProceduralFile}.
 */
export const useSubmitProceduralFile = () => {
  const { getFetchConfig } = useGetFetchConfig();
  const queryClient = useQueryClient();

  return useMutation<AdHocFile, Error, SubmitProceduralFileParams>(
    async ({ matterId, uploadId, name, mimeType, size, timeZone, jurisdiction }) => {
      const res = await fetch(
        `${APIBaseChronos}/api/matter/${matterId}/procedural-timeline/files`,
        getFetchConfig({
          method: FetchMethod.POST,
          data: {
            uploadId,
            name,
            mimeType,
            size,
            timeZone,
            jurisdiction,
          },
        }),
      );
      if (!res.ok) {
        throw new Error(await friendlySubmitError(res));
      }
      return res.json();
    },
    {
      onSuccess: (_, { matterId, mimeType, size }) => {
        queryClient.invalidateQueries([QueryEntity.ProceduralTimelineUploadEligibility, { matterId }]);
        trackCustomEvent(TrackingEventsV2.ProceduralTimelineFileUpload, { matterId, mimeType, size });
      },
    },
  );
};

/**
 * Map known backend error codes to user-facing copy. Anything we don't
 * recognise falls back to a generic line — never surface raw JSON.
 */
async function friendlySubmitError(res: Response): Promise<string> {
  const body = await res.json().catch(() => null);
  const code = typeof body === 'object' && body !== null ? String((body as Record<string, unknown>).message ?? '') : '';
  switch (code) {
    case 'duplicate_file':
      return "This file has already been processed for this matter. Make any changes you'd like to apply to the existing events directly.";
    case 'encrypted_file':
      return 'This file is password-protected or encrypted. Remove the password protection and try again.';
    default:
      return 'We couldn’t process this file. Please try again or contact support if the problem persists.';
  }
}
